import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Clock, Calendar, Search, Maximize2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// URLs that point at an actual media file — these can go straight into <video>.
const DIRECT_VIDEO_RE = /\.(mp4|m4v|webm|ogv|ogg|mov)(\?.*)?$/i;

// Bunny Stream gives us a *player page* URL (iframe.mediadelivery.net/embed/...), not a
// media file. Handing that to <video> yields text/html and playback dies silently, so
// anything that isn't a direct media file gets rendered in an <iframe> instead.
function getEmbedSrc(url) {
  if (!url) return null;
  if (DIRECT_VIDEO_RE.test(url)) return null;
  if (/iframe\.mediadelivery\.net\/(embed|play)\//i.test(url)) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}autoplay=false&preload=true&responsive=true`;
  }
  return url;
}

export default function ClassRecordings({ course }) {
  const [search, setSearch] = useState('');
  const recordings = course?.recordings || [];
  const [activeId, setActiveId] = useState(recordings[0]?.id ?? null);
  const videoRef = useRef(null);
  const playerWrapRef = useRef(null);

  const filtered = recordings.filter(r =>
    (r.title || '').toLowerCase().includes(search.toLowerCase())
  );

  const active = recordings.find(r => r.id === activeId) || recordings[0];

  const embedSrc = getEmbedSrc(active?.video_url);

  // Reload the <video> element when the source changes so the new src actually plays.
  // Skipped on the <iframe> path — it has no .load() and reloads itself on src change.
  useEffect(() => {
    if (!embedSrc && videoRef.current) videoRef.current.load();
  }, [active?.video_url, embedSrc]);

  const goFullscreen = () => {
    const el = playerWrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  };

  const blockContext = (e) => e.preventDefault();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Class Recordings</h2>
        <p className="text-muted-foreground text-sm mt-1">
          {recordings.length} {recordings.length === 1 ? 'session' : 'sessions'} recorded
        </p>
      </div>

      {recordings.length === 0 ? (
        <div className="glass rounded-2xl border border-white/80 p-8 text-center">
          <p className="text-muted-foreground text-sm">No recordings available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-5">
          {/* LEFT — Video player (70%) */}
          <div className="lg:col-span-7">
            <div
              ref={playerWrapRef}
              className="glass rounded-2xl border border-white/80 overflow-hidden shadow-sm bg-black"
            >
              <div className="relative aspect-video bg-black group/player">
                {embedSrc ? (
                  <iframe
                    key={active.id}
                    src={embedSrc}
                    title={active.title}
                    loading="lazy"
                    allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    className="w-full h-full border-0"
                  />
                ) : active?.video_url ? (
                  <video
                    ref={videoRef}
                    key={active.id}
                    src={active.video_url}
                    poster={active.thumbnail_url || undefined}
                    controls
                    controlsList="nodownload noremoteplayback noplaybackrate"
                    disablePictureInPicture
                    onContextMenu={blockContext}
                    className="w-full h-full object-contain bg-black"
                  >
                    Sorry, your browser does not support embedded videos.
                  </video>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
                    Video unavailable
                  </div>
                )}
                <button
                  type="button"
                  onClick={goFullscreen}
                  title="Toggle fullscreen"
                  className="absolute top-3 right-3 z-10 w-9 h-9 rounded-lg bg-black/55 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm opacity-0 group-hover/player:opacity-100 transition-opacity"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>

              {active && (
                <div className="p-4 bg-white">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <h3 className="font-heading font-bold text-base leading-tight">
                        {active.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                        {active.module && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                            {active.module}
                          </span>
                        )}
                        {active.recorded_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(active.recorded_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                        {active.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {active.duration}
                          </span>
                        )}
                        <span>{active.views || 0} views</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Recordings list (30%) */}
          <div className="lg:col-span-3">
            <div className="glass rounded-2xl border border-white/80 shadow-sm flex flex-col h-full">
              <div className="p-3 border-b border-black/5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search recordings..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9 rounded-xl border-black/15 text-sm"
                  />
                </div>
              </div>

              <div className="p-2 space-y-2 overflow-y-auto max-h-[560px]">
                {filtered.length === 0 ? (
                  <p className="text-muted-foreground text-xs text-center py-6">
                    No recordings match "{search}".
                  </p>
                ) : (
                  filtered.map((rec, i) => {
                    const isActive = rec.id === active?.id;
                    return (
                      <motion.button
                        key={rec.id}
                        type="button"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        onClick={() => setActiveId(rec.id)}
                        className={`w-full text-left rounded-xl border overflow-hidden transition-all flex gap-2.5 p-2 ${
                          isActive
                            ? 'bg-primary/10 border-primary/40 shadow-sm'
                            : 'bg-white border-black/5 hover:bg-black/[0.03] hover:border-black/10'
                        }`}
                      >
                        <div className="relative w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-black">
                          {rec.thumbnail_url ? (
                            <img
                              src={rec.thumbnail_url}
                              alt={rec.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900" />
                          )}
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <Play className="w-4 h-4 text-white fill-white" />
                          </div>
                          {rec.duration && (
                            <span className="absolute bottom-0.5 right-0.5 text-[9px] font-semibold px-1 rounded bg-black/70 text-white">
                              {rec.duration}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1 py-0.5">
                          <h4 className={`font-semibold text-xs leading-snug line-clamp-2 ${isActive ? 'text-primary' : ''}`}>
                            {rec.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                            {rec.module && <span className="font-medium">{rec.module}</span>}
                            {rec.recorded_date && (
                              <span>
                                {new Date(rec.recorded_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
